import {
    ApplicationRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Injector,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { NgForOf, NgIf } from '@angular/common';
import { SeSpriteComponent } from '../se-sprite/se-sprite.component';
import { SeClickOutsideDirective } from '../../lib/se-clickoutside.directive';

@Component({
    selector: 'se-combobox',
    templateUrl: './se-combobox.component.html',
    styleUrls: ['./se-combobox.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeComboBoxComponent),
            multi: true,
        },
    ],
    standalone: true,
    imports: [FormsModule, NgForOf, SeSpriteComponent, NgIf, SeClickOutsideDirective],
})
export class SeComboBoxComponent implements OnInit, ControlValueAccessor {
    // data members

    elementPosition = { left: 0, top: 0, width: 0 };

    @ViewChild('dropDownTemplate')
    dropDownTemplate: TemplateRef<any>;

    @ViewChild('dropDownContent')
    dropDownContent: ElementRef;

    @ViewChild('dropDownContainer')
    dropDownContainer: ElementRef;
    @Input()
    zIndex: number = 20000;
    filteredItems: any[];
    index: number = 0;
    activeItem: any = null;
    @Input()
    selectedItem: any = null;
    @Output()
    selectedItemChange = new EventEmitter<any>();
    @Input()
    valuePath: string;
    @Input()
    viewPath: string;
    @Input()
    title: string;
    @Input()
    readOnly = false;
    @Input()
    enableDelete = false;
    currentText = '';
    private dropDownPortal: TemplatePortal;
    private dropDownPortalHost: DomPortalOutlet;

    constructor(
        private appRef: ApplicationRef,
        private injector: Injector,
        private viewContainerRef: ViewContainerRef,
    ) {}

    private _isOpen: boolean = false;

    get isOpen() {
        return this._isOpen;
    }

    @Input()
    set isOpen(isOpen: boolean) {
        this.onIsOpen(isOpen);
    }

    private _items: any[];

    get items() {
        return this._items;
    }

    @Input()
    set items(items) {
        this._items = items;
        this.initFilteredItems();
    }

    // constructor

    propagateChange = (_: any) => {
        /* This is intentional */
    };

    // public functions

    ngOnInit() {
        this.calculatePosition();
        this.initFilteredItems();
    }

    ngAfterViewInit() {
        this.initDropDown();
    }

    ngOnDestroy() {
        this.dropDownPortal.detach();
    }

    @HostListener('window:resize')
    onResize() {
        this.calculatePosition();
    }

    ngOnChange() {
        this.initFilteredItems();
    }

    writeValue(obj: any): void {
        if (obj == null || this.valuePath == null || this.valuePath == '') {
            this.selectedItem = obj;
        } else {
            let selections =
                this.items == null ? null : this.items.filter((item) => item[this.valuePath] == obj[this.valuePath]);
            this.selectedItem = selections == null || selections.length == 0 ? null : selections[0];
        }

        this.synchronizeCurrentText();
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        /* This is intentional */
    }

    public onToggle() {
        this.isOpen = !this.isOpen;
    }

    public onDelete() {
        this.currentText = '';
        this.textChange(this.currentText);
        this.onSelection(null);
    }

    public onToggleFromInput() {
        if (!this.readOnly) {
            return;
        }

        this.onToggle();
    }

    public onSelection(i) {
        this.isOpen = false;
        this.selectedItem = i;
        this.index = this.filteredItems.indexOf(i);

        this.synchronizeCurrentText();

        this.propagateChange(this.selectedItem);
        this.selectedItemChange.emit(this.selectedItem);
    }

    public onTextChange(filteredText: string) {
        this.textChange(filteredText);
        this.isOpen = true;
    }

    public onArrowDown() {
        this.scrollActiveItemIntoView(false);

        if (this.isOpen && this.index < this.filteredItems.length - 1) {
            this.index++;
            this.activeItem = this.filteredItems[this.index];
        }

        this.isOpen = true;

        return false;
    }

    public onArrowUp() {
        this.scrollActiveItemIntoView(false);

        if (this.isOpen && this.index > 0) {
            this.index--;
            this.activeItem = this.filteredItems[this.index];
        }

        this.isOpen = true;

        return false;
    }

    public onEnter() {
        let isOpen = this.isOpen;

        this.onSelection(this.activeItem);

        if (isOpen) {
            return false;
        }
    }

    public onEscape() {
        this.isOpen = false;
    }

    public getSelectedViewPath(viewPath) {
        return this.selectedItem == null ? this.currentText : this.selectedItem[viewPath];
    }

    onClickOutside(target: HTMLElement) {
        if (target && target.classList) {
            let iter = target;
            do {
                if (iter.classList.contains('se-dropdown')) {
                    return;
                }
                iter = iter.parentElement;
            } while (iter !== null && iter.nodeType === 1);
        }

        this.isOpen = false;

        this.synchronizeCurrentText();
        this.textChange(this.currentText);
    }

    // private functions

    private initFilteredItems() {
        this.filteredItems = this.items;
    }

    private textChange(newText: string) {
        this.currentText = newText;

        if (!this.readOnly) {
            this.filteredItems = this.items.filter((src) => {
                let text: string = this.viewPath == null || this.viewPath == '' ? src : src[this.viewPath];
                return text.toLowerCase().startsWith(newText == null ? '' : newText.toLowerCase());
            });
        }

        this.synchronizeSelectedItem();
        this.synchronizeActiveItem();
    }

    private onIsOpen(isOpen: boolean) {
        if (!this._isOpen) {
            this.synchronizeActiveItem();
        }

        this.calculatePosition();

        this._isOpen = isOpen;
    }

    private synchronizeCurrentText() {
        if (this.selectedItem != null) {
            this.currentText =
                this.viewPath == null || this.viewPath == '' ? this.selectedItem : this.selectedItem[this.viewPath];
        } else {
            this.currentText = '';
        }
    }

    private synchronizeSelectedItem() {
        if (this.filteredItems.length == 1) {
            this.selectedItem = this.filteredItems[0];
        } else if (!this.readOnly) {
            this.selectedItem = null;
        }
    }

    private synchronizeActiveItem() {
        if (this.selectedItem == null || this.filteredItems.length == 0) {
            this.activeItem = null;
            this.index = -1;
            return;
        }

        let selectedFilteredIndex = this.filteredItems.indexOf(this.selectedItem);
        if (selectedFilteredIndex != -1) {
            this.activeItem = this.selectedItem;
            this.index = selectedFilteredIndex;
        } else {
            let selectedIndex = this.items.indexOf(this.selectedItem);
            if (selectedIndex >= this.filteredItems.length) {
                this.activeItem = this.filteredItems[this.filteredItems.length - 1];
                this.index = this.filteredItems.length - 1;
            } else {
                this.activeItem = this.filteredItems[0];
                this.index = 0;
            }
        }
    }

    private calculatePosition() {
        if (this.dropDownContainer.nativeElement instanceof HTMLElement && this.dropDownContent) {
            const { top, bottom, left, width } = this.dropDownContainer.nativeElement.getBoundingClientRect();

            this.elementPosition = {
                left,
                top: bottom,
                width,
            };

            setTimeout(() => {
                let dropDownContentRect = this.dropDownContent.nativeElement.getBoundingClientRect();
                if (bottom + dropDownContentRect.height > document.body.clientHeight)
                    this.elementPosition.top = top - dropDownContentRect.height;
            });
        }
    }

    private initDropDown() {
        if (!this.dropDownPortalHost) {
            this.dropDownPortalHost = new DomPortalOutlet(
                document.querySelector('body'),
                undefined,
                this.appRef,
                this.injector,
            );
        }

        this.dropDownPortal = new TemplatePortal(this.dropDownTemplate, this.viewContainerRef);

        this.dropDownPortal.attach(this.dropDownPortalHost);
    }

    private scrollActiveItemIntoView(alignToTop = true) {
        setTimeout(() => {
            let element = this.dropDownContent.nativeElement.querySelector('.active');
            if (element) {
                element.scrollIntoView(alignToTop);
            }
        });
    }
}
